import { useState, useEffect } from "react";
import AttachmentDiscussion from "./AttachmentDiscussion";
import DragDropUpload from "./DragDropUpload";
import Utils from "../../Utils";
import _ from "lodash";
import Swal from "sweetalert2";
import axios from "axios";
import moment from "moment";

const Shotlists = ({ attachments = [], canUpload, psId, onUpload }) => {
  const [slopen, setSlopen] = useState(0);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const downloadAttachment = (attachment) => {
    Swal.fire({
      title: "Downloading...",
      text: "Please wait while your file is being downloaded",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    axios
      .get(Utils.apiUrl(`photoshoot/attachment/${attachment.id}`), {
        responseType: "arraybuffer",
        ...Utils.apiHeaders(),
      })
      .then((res) => {
        const bufferData = res.data;
        const byteArray = new Uint8Array(bufferData);

        const blob = new Blob([byteArray], {
          type: res.headers["content-type"] || "application/octet-stream",
        });

        const filename = attachment.filename;

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        URL.revokeObjectURL(url);
        document.body.removeChild(a);

        // ✅ SUCCESS ALERT
        Swal.fire({
          icon: "success",
          title: "Downloaded!",
          text: `${filename} downloaded successfully.`,
          timer: 2000,
          showConfirmButton: false,
        });
      })
      .catch((err) => {
        console.error("Download failed:", err);

        // ❌ ERROR ALERT
        Swal.fire({
          icon: "error",
          title: "Download Failed",
          text: "Unable to download file. Please try again.",
        });
      });
  };

  useEffect(() => {
    setOpen(attachments.length <= 0);
  }, [attachments]);

  const onUploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true); // ⬅ start loading

    const formData = new FormData();
    formData.append("psid", psId);

    Array.from(files).forEach((file) => {
      formData.append("attachments", file);
    });

    try {
      const res = await axios.post(Utils.apiUrl("photoshoot/attachment"), formData, {
        ...Utils.apiHeaders(),
        headers: {
          ...Utils.apiHeaders().headers,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.success) {
        Swal.fire("Uploaded!", res.data.message, "success");
        onUpload(); // Refresh parent data after successful upload
      } else {
        Swal.fire("Upload failed", res.data.message, "error");
      }
    } catch (err) {
      Swal.fire("Error", "Failed to upload attachments", "error");
    } finally {
      setUploading(false); // ⬅ stop loading (always)
    }
  };

  return (
    <>
      <div className="form-section">
        {/* Header */}
        <div className="section-heading flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="fa fa-paperclip text-gray-500"></i>
            Shot Lists
          </div>

          {canUpload() && (
            <button className="btn p-1 text-sm rounded bg-blue-600 hover:bg-blue-700" title="Upload Shotlist" onClick={() => setOpen(!open)}>
              <i className={`fas text-white transition-transform ${open ? "fa-minus" : "fa-plus "}`}></i>
            </button>
          )}
        </div>

        {/* Accordion */}
        {canUpload() && (
          <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-96 mt-4 mb-4" : "max-h-0"}`}>
            <DragDropUpload onUpload={onUploadFiles} />
            {uploading && <p className="text-sm text-gray-500 mt-2 text-center">Uploading files...</p>}
          </div>
        )}

        <div className="form-group">
          {/* Loop for attachments */}
          {attachments != undefined &&
            attachments.length > 0 &&
            attachments.map((attachment, index) => (
              <>
                <div className="attachment-item">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {attachment.usertype.toUpperCase()}
                      </div>
                      <i className="fas fa-file-alt text-gray-900"></i>
                      <span>Document {index + 1}</span>

                      <button className="btn p-1 text-sm rounded ml-2" title="Download Shotlist file" onClick={() => downloadAttachment(attachment)}>
                        <i className="fas fa-download"></i>
                      </button>
                      {attachment.created_at && <span className="text-xs text-gray-500 ml-2">{moment(attachment.created_at).fromNow()}</span>}
                    </div>
                    <button
                      className="btn p-1 text-sm rounded"
                      title="Toggle Discussion"
                      onClick={() => setSlopen(slopen === attachment.id ? 0 : attachment.id)}
                    >
                      <i className={`fas fa-caret-${slopen === attachment.id ? "up" : "down"}`}></i>
                    </button>
                  </div>

                  <div className={slopen === attachment.id ? "block" : "hidden"}>
                    <AttachmentDiscussion attachment={attachment} psId={psId} canUpload={canUpload} />
                  </div>
                </div>
              </>
            ))}
          {/* If no attachments uploaded */}
          {attachments != undefined && attachments.length == 0 && (
            <>
              <div className="attachment-item">No Shotlist uploaded yet!</div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Shotlists;
